'use client'

import { usePathname } from 'next/navigation'
import { Globe } from 'lucide-react'
import { getLocaleAlternate } from '@/lib/i18n/locale-alternates'

interface LocaleSwitcherProps {
  className?: string
}

/**
 * Language switcher for pages that have a translated counterpart.
 *
 * Renders nothing when the current route has no alternate, so it can sit in
 * the shared header unconditionally without promising translations that do
 * not exist.
 *
 * A plain <a>, never next/link: `lang` and `dir` live on <html> and are
 * resolved on the server from the request path, so a client-side transition
 * would land on the target document still carrying the previous language's
 * direction. Crossing a language boundary is a document change, so it takes a
 * document navigation. (Same reasoning as the in-page link on /ar/radar.)
 *
 * The globe plus the language's own endonym is the convention users scan for;
 * the previous bare "العربية" text gave a reader no signal that it was a
 * language control at all.
 */
export function LocaleSwitcher({ className = '' }: LocaleSwitcherProps) {
  const pathname = usePathname()
  const alternate = getLocaleAlternate(pathname)

  if (!alternate) return null

  return (
    <a
      href={alternate.href}
      hrefLang={alternate.locale}
      lang={alternate.locale}
      dir={alternate.dir}
      aria-label={alternate.ariaLabel}
      className={`inline-flex min-h-11 items-center gap-2 px-2 text-sm font-medium text-foreground transition-opacity duration-200 hover:opacity-70 ${className}`}
    >
      <Globe className='h-4 w-4 shrink-0' aria-hidden='true' />
      <span className='leading-none'>{alternate.label}</span>
    </a>
  )
}

export default LocaleSwitcher
